// ACHTUNG!!! SEO-kritische Komponente, Direkte Importe und Backendaufrufe sollten so gering wie nur möglich gehalten werden 


import { Component, ComponentRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { delayHelper } from '@commonHelpers/delay-helper';
import { isNullish } from '@commonHelpers/math-utils';
import { LogoService } from '@commonServices/logo.service';
import { CatalogViewCatalogInformationDto } from '@interfaces/HttpClient/CatalogApiPublicModels';
import { ICatalogQueryParams } from '@interfaces/iCatalogRoutingParams';
import { CatalogViewService } from '@services/catalog-view.service';
import { TranslationService } from '@services/translation.service';
import { Subscription } from 'rxjs';
import { MobileBarComponent, MobileButtonType } from "../../../components/mobile-bar/mobile-bar.component";
import { CatalogNotFoundComponent } from "../../errorpages/catalog-not-found/catalog-not-found.component";
import { MobileHeaderComponent } from "../../header/mobile-header/mobile-header.component";
import { CatalogMobileDetailsComponent } from '../catalog-mobile-details/catalog-mobile-details.component';
import { CatalogMobileInfoComponent } from '../catalog-mobile-info/catalog-mobile-info.component';
import { CatalogMobileNewsComponent } from '../catalog-mobile-news/catalog-mobile-news.component';
import { CatalogMobileSearchTreeComponent } from '../catalog-mobile-search-tree/catalog-mobile-search-tree.component';
import { CatalogMobileSearchComponent } from '../catalog-mobile-search/catalog-mobile-search.component';
import { CatalogMobileTreeComponent } from '../catalog-mobile-tree/catalog-mobile-tree.component';

@Component({
    selector: 'app-catalog-mobile',
    templateUrl: './catalog-mobile.component.html',
    styleUrls: ['./catalog-mobile.component.scss'],
    providers: [CatalogViewService],
    imports: [
        RouterModule,
        MobileBarComponent,
        MobileHeaderComponent,
        CatalogMobileSearchTreeComponent,
        CatalogMobileTreeComponent,
        CatalogMobileInfoComponent,
        CatalogMobileDetailsComponent,
        CatalogMobileSearchComponent,
        CatalogMobileNewsComponent,
        CatalogNotFoundComponent
    ]
})
export class CatalogMobileComponent implements OnInit, OnDestroy {
	public catalogKey: string;
	public currentMobileView: MobileButtonType;
	public catalog: CatalogViewCatalogInformationDto;
	private componentRef: ComponentRef<any>
	public mobileBarButtons = [MobileButtonType.CatalogTree, MobileButtonType.CatalogInfo, MobileButtonType.CatalogDetails, MobileButtonType.SearchInput];
	public disabledMobileBarButtons = [];
	public initMobileBarButton = MobileButtonType.CatalogDetails;
	public mobileButtonType = MobileButtonType;
	public isSearch = false;
	private routeSubscription: Subscription;
	private queryParamSubscription: Subscription;
	public validCatalog = true;
	@ViewChild("mobileBar", { static: true }) mobileBar: MobileBarComponent;


	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private catalogViewService: CatalogViewService,
		private titleService: Title,
		private meta: Meta,
		private translationService: TranslationService,
		private logoService: LogoService
	) {
	}

	async ngOnInit() {
		this.setInitView();
		const defaultButtons = this.mobileBarButtons;
		if (isNullish(this.route.snapshot.params.catalogItemGuid) && MobileButtonType[this.route.snapshot.queryParams.mobileView as string] === MobileButtonType.CatalogDetails) {
			// Ausnahmefall: Nutzer wählt Detailansicht aus, wechselt dann in den Desktopmodus, wählt den Wurzelknoten aus und wechselt dann wieder in den Mobilemodus
			// Zuvor gewählte Mobilansicht wird dann gelöscht 
			await this.router.navigate(
				[],
				{
					relativeTo: this.route,
					queryParams: { mobileView: null },
					queryParamsHandling: 'merge',
				}
			);
		}

		this.routeSubscription = this.route.params.subscribe(async params => {
			this.isSearch = params.view === "search";
			this.catalogKey = params.key;
			this.meta.updateTag({ name: 'image', content: this.logoService.getLargeLogosByKey(this.catalogKey).optimizedSrc });
			this.componentRef?.setInput("catalogKey", this.catalogKey);
			this.disabledMobileBarButtons = isNullish(params.catalogItemGuid) ? [MobileButtonType.CatalogDetails] : [];
			this.catalog = await this.catalogViewService.getCatalogInformation(this.catalogKey);
			if (!isNullish(this.catalog)) {
				this.validCatalog = true;
				if (isNullish(params.catalogItemGuid)) {
					this.titleService.setTitle(this.translationService.getByKey("browsertab.catalog-view", { name: this.catalog.title }));
					this.meta.updateTag({ name: 'title', content: this.catalog.title });
					this.meta.updateTag({ name: 'description', content: this.catalog.catalogMetaDescription });
				} else {
					void await this.catalogViewService.getCatalogItemMeta(params.catalogItemGuid).then(catalogItemMetaData => {
						if (!isNullish(catalogItemMetaData) && this.route.snapshot.params.catalogItemGuid === catalogItemMetaData.guid) {
							const title = catalogItemMetaData.title;
							this.titleService.setTitle(`${this.catalog.title} - ${title}`);
							this.meta.updateTag({ name: 'title', content: title });
							this.meta.updateTag({ name: 'description', content: catalogItemMetaData.description });
						}
					});

				}
				const additionalButtons = [];
				if (this.catalog.hasContactPersons) {
					additionalButtons.push(MobileButtonType.CatalogContact);
				}
				if (this.catalog.hasNews) {
					additionalButtons.push(MobileButtonType.Infocenter);
				}
				this.mobileBarButtons = defaultButtons.concat(additionalButtons)
				await this.translationService.switchCatalogLanguage(this.catalog.language);
				if (!isNullish(this.catalog)) {
					this.componentRef?.setInput("catalog", this.catalog);

				}
				// Workaround für ngx-translateBug
				delayHelper(async () => this.translationService.switchCatalogLanguage(this.catalog.language))
			} else {
				this.validCatalog = false;
			}


		});

		this.queryParamSubscription = this.route.queryParams.subscribe(async (params: ICatalogQueryParams) => {
			if (isNullish(this.route.snapshot.queryParams.mobileView)) {
				this.setInitView();
			} else {
				const currentMobileView = MobileButtonType[params.mobileView];
				if (isNullish(this.currentMobileView) || currentMobileView !== this.currentMobileView) {
					this.currentMobileView = currentMobileView;
				}
			}


		})
	}

	private setInitView() {
		if (isNullish(this.route.snapshot.queryParams.mobileView)) {
			if (isNullish(this.route.snapshot.params.catalogItemGuid)) {
				this.mobileBar.buttonClicked(MobileButtonType.CatalogInfo)
			} else {
				this.mobileBar.buttonClicked(MobileButtonType.CatalogDetails)
			}
		}
	}


	async ngOnDestroy() {
		this.routeSubscription?.unsubscribe();
		this.queryParamSubscription?.unsubscribe();
	}

}
